/**
 * Pure helpers behind the generative-UI renderers — the JSX-free half so the
 * directive/media detection and form round-trip logic are unit-testable without
 * a DOM. The agent emits {@link UiDirective}s (and inline media refs) as
 * `tool-result` outputs on the run channel; this module extracts + validates
 * them, and turns a `form` directive's fields into controlled state, a validity
 * check, and the serialized value echoed back to the parked run.
 */
import { MediaJobStatus, MediaKind, UiDirective, type UiFormField } from '@4f/shared';
import { z } from 'zod';

/**
 * The inline handle a `generate_image`/`generate_video` tool returns — the
 * client renders it as a "generating…" placeholder that the WS relay later swaps
 * for the finished asset. A permissive mirror of the backend `GeneratedMediaRef`
 * (only the fields the placeholder needs are required).
 */
export const MediaRef = z.object({
  jobId: z.string().min(1),
  girlfriendId: z.string().optional(),
  kind: MediaKind,
  status: MediaJobStatus,
  segmentTotal: z.number().int().positive().optional(),
});
export type MediaRef = z.infer<typeof MediaRef>;

/** Unwrap a value that may be the payload itself or `{ directive/ref: payload }`. */
function candidates(value: unknown): unknown[] {
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return [value, record.directive, record.ref, record.output].filter((v) => v !== undefined);
  }
  return [value];
}

/**
 * Extract a {@link UiDirective} from a tool output, or `null`. `UiDirective`'s
 * `kind` literals are disjoint from a media ref's `kind` (`image`/`video`), so a
 * media handle never misparses as a directive.
 */
export function extractDirective(value: unknown): UiDirective | null {
  for (const candidate of candidates(value)) {
    const parsed = UiDirective.safeParse(candidate);
    if (parsed.success) {
      return parsed.data;
    }
  }
  return null;
}

/** Extract an inline {@link MediaRef} from a tool output, or `null`. */
export function extractMediaRef(value: unknown): MediaRef | null {
  for (const candidate of candidates(value)) {
    const parsed = MediaRef.safeParse(candidate);
    if (parsed.success) {
      return parsed.data;
    }
  }
  return null;
}

/** True when a directive parks the run awaiting a user answer (form/choice/file). */
export function isInputDirective(directive: UiDirective): boolean {
  return directive.kind === 'form' || directive.kind === 'choice' || directive.kind === 'file';
}

/** A single form field's controlled value — a string for text/number/select, a boolean for a toggle. */
export type FormValue = string | boolean;

/** Build the initial controlled state for a `form` directive's fields. */
export function initialFormValues(fields: readonly UiFormField[]): Record<string, FormValue> {
  const values: Record<string, FormValue> = {};
  for (const field of fields) {
    switch (field.type) {
      case 'toggle':
        values[field.name] = field.default;
        break;
      case 'select':
        values[field.name] = field.options[0]?.value ?? '';
        break;
      default:
        values[field.name] = '';
        break;
    }
  }
  return values;
}

/** The names of every required field left empty / out of range — empty ⇒ the form is submittable. */
export function invalidFields(
  fields: readonly UiFormField[],
  values: Record<string, FormValue>,
): string[] {
  const missing: string[] = [];
  for (const field of fields) {
    const value = values[field.name];
    if (field.type === 'toggle') {
      continue;
    }
    if (field.type === 'number') {
      if (!field.required && value === '') {
        continue;
      }
      const num = Number(value);
      const empty = value === '' || value === undefined;
      const outOfRange =
        (field.min !== undefined && num < field.min) ||
        (field.max !== undefined && num > field.max);
      if (empty || Number.isNaN(num) || outOfRange) {
        missing.push(field.name);
      }
      continue;
    }
    if (field.required && (value === undefined || String(value).trim() === '')) {
      missing.push(field.name);
    }
  }
  return missing;
}

/**
 * Serialize the controlled state into the value echoed back to the run: number
 * fields become numbers, toggles booleans, everything else its string. Only
 * declared fields are included, so stray state never leaks to the server.
 */
export function serializeFormValues(
  fields: readonly UiFormField[],
  values: Record<string, FormValue>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const field of fields) {
    const value = values[field.name];
    if (field.type === 'number') {
      out[field.name] = value === '' || value === undefined ? null : Number(value);
    } else if (field.type === 'toggle') {
      out[field.name] = Boolean(value);
    } else {
      out[field.name] = value ?? '';
    }
  }
  return out;
}
